import { Fragment, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, BadgeCheck, BookOpen, Palette, Smile, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PromoImg } from '@/components/storybook/marketing/PromoImg';
import { SamplePhotosTag } from '@/components/storybook/marketing/SectionHeading';
import {
  APPROVED_DRAWING,
  APPROVED_DRAWING_STYLE,
  DRAWN_PEOPLE,
  SAMPLE_PHOTOS,
  STYLE_EMOJI,
} from '@/components/storybook/marketing/promo';
import { trackEvent } from '@/lib/analytics';
import { hasFinePointer, isSmallScreen } from '@/lib/motion';

// AI Storybook teaser on the landing page. The rest of the site is dark and
// grown-up; this card is a bright, pastel window into the children's product
// (it borrows the storybook palette via .storybook-theme), and links out to
// /storybook where the product actually lives.
//
// Real pictures, not mockups (2026-09-27). The owner: "it's not quite clear
// just by looking at it what it provides ... actually putting their faces in
// the story". So the picture IS the pitch: two sample photos, an arrow, and
// the storybook cover our engine drew from them, then the four art styles a
// parent picks from. Only the one drawing the owner approved as a clear
// likeness is shown (2026-09-28, components/storybook/marketing/promo.ts):
// the engine's unretouched 3D cover of Mia and her mom. The other styles are
// named, never shown with a stand-in picture. The "photos" are SYNTHETIC
// people of a made-up family, never customers, so they sit under a "Sample
// photos" label and the caption says so. Tom (the dad) is not in the cover,
// so his photo is not here.
//
// Two points at first glance (owner, 2026-09-28): the title says the
// pictures are drawn from YOUR photos to look like your child, and the chip
// right under it says the book can be ordered printed, a keepsake to cherish
// for years. The bullets keep the PDF next to the spiral-bound print. The
// title, the chip and the bullets wrap evenly ([text-wrap:balance]), so a key
// word like "photos." is never left alone on the last line of a phone.

const BULLETS = [
  { key: 'personal', icon: Smile, tint: 'bg-secondary text-secondary-foreground' },
  { key: 'family', icon: Users, tint: 'bg-[hsl(265_80%_93%)] text-[hsl(262_50%_40%)]' },
  { key: 'illustrations', icon: Palette, tint: 'bg-[hsl(330_85%_92%)] text-[hsl(330_60%_38%)]' },
  { key: 'approve', icon: BadgeCheck, tint: 'bg-[hsl(150_60%_88%)] text-[hsl(160_60%_24%)]' },
  { key: 'print', icon: BookOpen, tint: 'bg-[hsl(45_95%_85%)] text-[hsl(30_70%_28%)]' },
] as const;

/** Each print sits a little crooked, like photos dropped on a table. */
const PRINT_TILT = ['-rotate-3', 'rotate-2 ml-[8%]'];
// Each print overlaps the one above it by a few pixels, so the earlier print
// is stacked on top: the name label at its foot stays whole, and the later
// print only loses a sliver of its white border.

/**
 * "Their name. Their face. Their adventure." broke as "... Their /
 * adventure." in the md+ column. Short sentences are kept whole, so a title
 * like that only breaks between them; a long one wraps as usual. (Since
 * 2026-09-28 the title is one sentence, "Their face, drawn from your
 * photos.", so it simply wraps.)
 */
function keepSentences(text: string): ReactNode {
  const parts = text.split(/(?<=[.!?])\s+/);
  if (parts.length < 2 || parts.some((part) => part.length > 28)) return text;
  return parts.map((part, i) => (
    <Fragment key={i}>
      {i > 0 && ' '}
      <span className="whitespace-nowrap">{part}</span>
    </Fragment>
  ));
}

/** A little white label on a picture ("The cover"). */
function PictureLabel({ children, className = 'left-2 sm:left-3' }: { children: string; className?: string }) {
  return (
    <span className={`absolute top-2 rounded-full bg-white/90 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-[#20294a] shadow-sm sm:top-3 sm:px-2.5 sm:py-1 sm:text-[11px] ${className}`}>
      {children}
    </span>
  );
}

/**
 * Photo -> storybook, at a glance: Mia's and her mom's sample photos as
 * prints, an arrow, the 3D cover drawn from them, and the four art styles
 * underneath (the cover's own style marked).
 */
function StorybookPitchVisual() {
  const { t } = useTranslation();
  return (
    <figure className="relative mx-auto w-full max-w-[26rem]" aria-labelledby="storybook-pitch-visual-caption">
      <div className="relative flex items-center">
        <div className="relative z-10 -mr-[7%] flex w-[31%] shrink-0 flex-col items-start">
          <SamplePhotosTag label={t('storybookPage.promo.samplePhotos')} className="mb-2 max-[359px]:px-2 max-[359px]:text-[10px]" />
          {DRAWN_PEOPLE.map((person, i) => (
            <div
              key={person}
              style={{ zIndex: DRAWN_PEOPLE.length - i }}
              className={`relative w-[92%] rounded-xl bg-white p-1 pb-0.5 shadow-[0_10px_24px_rgba(32,41,74,0.2)] ring-1 ring-black/5 ${PRINT_TILT[i]} ${i > 0 ? '-mt-1.5' : ''}`}
            >
              <PromoImg
                image={SAMPLE_PHOTOS[person]}
                sizes="(min-width: 480px) 125px, 28vw"
                className="aspect-square w-full rounded-lg object-cover"
              />
              <span className="storybook-display block truncate px-0.5 pb-0.5 pt-0.5 text-center text-[11px] font-semibold leading-tight text-[#20294a] sm:text-xs">
                {t(`storybookPage.promo.people.${person}`)}
              </span>
            </div>
          ))}
        </div>

        <div className="relative min-w-0 flex-1">
          <div className="relative">
            {/* The page block behind the cover. */}
            <div aria-hidden="true" className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-3xl bg-[#f3ead9] shadow-[0_10px_24px_rgba(32,41,74,0.18)]" />
            <div className="relative overflow-hidden rounded-3xl bg-[#cde8f2] shadow-[0_18px_40px_rgba(32,41,74,0.22)] ring-1 ring-black/5">
              <PromoImg
                image={APPROVED_DRAWING}
                sizes="(min-width: 480px) 310px, 72vw"
                className="aspect-square w-full object-cover"
              />
              {/* On the right: the prints overlap the cover's left edge. */}
              <PictureLabel className="right-2 sm:right-3">{t('storybook.visual.cover')}</PictureLabel>
            </div>
          </div>
          {/* Just right of the prints, beside Mia's photo, so it never
              covers a name label ("Mia, 5 años" at 360px included). */}
          <span
            aria-hidden="true"
            className="absolute left-[15%] top-[44%] z-20 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] border-white bg-primary text-primary-foreground shadow-lg sm:h-11 sm:w-11"
          >
            <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
          </span>
        </div>
      </div>

      {/* One art style (owner, 2026-09-29): 3D, the style the cover above is drawn in. */}
      <p className="mt-6 flex items-center justify-center gap-2 rounded-full bg-white/85 px-4 py-2 text-center text-sm font-semibold leading-snug text-[#20294a] shadow-sm ring-1 ring-black/5 [text-wrap:balance] sm:mt-7">
        <span aria-hidden="true">{STYLE_EMOJI[APPROVED_DRAWING_STYLE]}</span>
        {t('storybook.visual.styleLine')}
      </p>

      <figcaption id="storybook-pitch-visual-caption" className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">
        {t('storybook.visual.caption')}
      </figcaption>
    </figure>
  );
}

// The pastel card on its own (2026-09-24): it is the landing hero's pitch for
// the compass's storybook stop, and StorybookSection below wraps it with the
// section heading for any page that wants the standalone block.
export function StorybookPitch({ titleAs: Title = 'h3' }: { titleAs?: 'h2' | 'h3' }) {
  const { t } = useTranslation();

  return (
    <motion.div
      // On a phone or a touch screen the card is simply there (2026-09-25):
      // a busy phone held it at the opacity-0 start, a blank frame right
      // under the h1. The fade-up stays where a mouse is in use.
      initial={isSmallScreen() || !hasFinePointer() ? false : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5 }}
      // No .glass-card and no blur-2xl orbs (2026-09-24): a backdrop-filter
      // card around two filter:blur layers inside a whileInView transform is
      // the stack that stalls painting on iOS — WebKit left solid black and
      // white blocks over the hero text. Same frame, drawn without filters;
      // the backdrop blur returns only where a mouse is in use.
      className="mx-auto max-w-5xl rounded-2xl border border-border/90 bg-card/75 p-1.5 shadow-[var(--shadow-card)] [background-image:var(--gradient-card)] sm:p-2 [@media(hover:hover)_and_(pointer:fine)]:backdrop-blur-xl"
      data-testid="storybook-pitch"
    >
      <div className="storybook-theme relative overflow-hidden rounded-[1.1rem] bg-[linear-gradient(135deg,#fff5e4_0%,#ffe4ec_48%,#e1f1ff_100%)] p-6 max-[359px]:p-4 sm:p-10">
        <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.55),rgba(255,255,255,0))]" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-14 h-72 w-72 rounded-full bg-[radial-gradient(closest-side,hsl(45_95%_80%/0.5),hsl(45_95%_80%/0))]" />

        {/* Phones read it top to bottom as badge + title, then the picture,
            then the pitch: the photo-to-storybook picture is the point, so
            it comes before five bullets, not after them. From lg up the copy
            is one column and the picture sits beside it (at md the column
            left the picture too narrow for its captions). */}
        <div className="relative grid gap-x-10 gap-y-7 lg:grid-cols-[1.05fr_0.95fr] lg:grid-rows-[auto_auto] lg:gap-y-3">
          <div className="lg:col-start-1 lg:row-start-1 lg:self-end">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary shadow-sm">
              <span aria-hidden="true">✨</span>
              {t('storybook.badge')}
            </span>
            <Title className="mt-4 text-2xl font-semibold leading-tight [text-wrap:balance] sm:text-3xl">{keepSentences(t('storybook.cardTitle'))}</Title>
            {/* The owner (2026-09-28): the pitch must say at first glance
                that the pictures are drawn from the family's own photos (the
                title) AND that the book can be ordered printed, a keepsake
                for years. This chip sits right under the title, so on a phone
                it is read before the picture, not five bullets later. */}
            <p
              data-testid="storybook-print-chip"
              className="mt-3 inline-flex max-w-full items-start gap-2 rounded-2xl bg-[hsl(45_95%_85%)] px-3 py-1.5 text-sm font-bold leading-snug text-[hsl(30_70%_28%)] shadow-sm"
            >
              <span aria-hidden="true">📖</span>
              <span className="min-w-0 [text-wrap:balance]">{t('storybook.printChip')}</span>
            </p>
          </div>

          <div className="lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center">
            <StorybookPitchVisual />
          </div>

          <div className="lg:col-start-1 lg:row-start-2 lg:self-start">
            <p className="text-base leading-relaxed text-muted-foreground">{t('storybook.cardBody')}</p>
            <ul className="mt-6 space-y-3">
              {BULLETS.map((bullet) => (
                <li key={bullet.key} className="flex items-center gap-3 text-[15px] font-semibold [text-wrap:balance] sm:text-sm">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${bullet.tint}`}>
                    <bullet.icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  {t(`storybook.bullets.${bullet.key}`)}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col items-start gap-2">
              {/* ≤359px (2026-09-25): px-8 (and the card's p-6) left "Create
                  a free preview" too narrow a line at 320, so it broke in two
                  with the arrow floating beside it. */}
              <Button asChild size="lg" className="w-full rounded-full sm:w-auto h-auto min-h-12 whitespace-normal py-3 text-center leading-snug max-[359px]:gap-1.5 max-[359px]:px-4 max-[359px]:text-[15px]">
                <Link to="/storybook" onClick={() => trackEvent('storybook_cta_clicked', { source: 'landing_section' })}>
                  {t('storybook.cta')}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
              <p className="text-xs text-muted-foreground">{t('storybook.note')}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function StorybookSection() {
  const { t } = useTranslation();

  return (
    <section id="storybook" className="relative py-24" aria-labelledby="landing-storybook-title">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-4 inline-block text-sm font-semibold uppercase tracking-wider text-primary"
          >
            {t('storybook.eyebrow')}
          </motion.span>
          <motion.h2
            id="landing-storybook-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mb-4 text-3xl font-bold sm:text-4xl md:text-5xl"
          >
            {t('storybook.titleStart')} <span className="gradient-text">{t('storybook.titleHighlight')}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mx-auto max-w-2xl text-lg text-muted-foreground"
          >
            {t('storybook.subtitle')}
          </motion.p>
        </div>

        <StorybookPitch />
      </div>
    </section>
  );
}
